import type { RowData } from "@tanstack/react-table";
import type { DataGridTable } from "../../columns/types";
import { getActiveRect } from "../../editing/cellRect";

/** No range touches the row. */
export const NO_RANGE = "";

/** The part of the range one row draws. Column indexes are in display order. */
export interface RowRange {
    left: number;
    right: number;
    isTop: boolean;
    isBottom: boolean;
    /** The range is just the active cell. */
    isSingle: boolean;
}

/** The active range's rectangle when it covers the row, else `null`. */
const getRowRect = <TData extends RowData>(
    table: DataGridTable<TData>,
    rowIndex: number,
) => {
    const rect = getActiveRect(table);
    return rect && rowIndex >= rect.top && rowIndex <= rect.bottom
        ? rect
        : null;
};

/**
 * What a row draws of the range (or the active cell), as a string so
 * `useSelector` re-renders only when the row's own part changes: growing a
 * range by one row changes the key of the old and the new bottom row only.
 */
export const getRowRangeKey = <TData extends RowData>(
    table: DataGridTable<TData>,
    rowIndex: number,
): string => {
    const rect = getRowRect(table, rowIndex);
    if (!rect) {
        return NO_RANGE;
    }
    const isSingle = rect.top === rect.bottom && rect.left === rect.right;
    return [
        rect.left,
        rect.right,
        rowIndex === rect.top ? "t" : "",
        rowIndex === rect.bottom ? "b" : "",
        isSingle ? "s" : "",
    ].join(":");
};

/**
 * The display-order columns of the row's range cells, as `left:right`, or
 * `NO_RANGE`. Unlike `getRowRangeKey`, growing a range down leaves the old
 * bottom row's span as it was.
 */
export const getRowRangeSpan = <TData extends RowData>(
    table: DataGridTable<TData>,
    rowIndex: number,
): string => {
    const rect = getRowRect(table, rowIndex);
    return rect ? `${rect.left}:${rect.right}` : NO_RANGE;
};

/**
 * The range's top and bottom edges the row holds, as `"top"`, `"bottom"`,
 * `"top bottom"`, or `NO_RANGE`. Rows mark them in data attributes, so range
 * styles need no `:has()` on the row.
 */
export const getRowRangeEdges = <TData extends RowData>(
    table: DataGridTable<TData>,
    rowIndex: number,
): string => {
    const rect = getRowRect(table, rowIndex);
    if (!rect) {
        return NO_RANGE;
    }
    return [
        rowIndex === rect.top ? "top" : undefined,
        rowIndex === rect.bottom ? "bottom" : undefined,
    ]
        .filter((edge) => edge !== undefined)
        .join(" ");
};

/** Whether the display-order column `index` is inside a `getRowRangeSpan`. */
export const isInRangeSpan = (span: string, index: number) => {
    if (span === NO_RANGE) {
        return false;
    }
    const [left, right] = span.split(":").map(Number);
    return index >= left && index <= right;
};

export const parseRowRangeKey = (key: string): RowRange | null => {
    if (key === NO_RANGE) {
        return null;
    }
    const [left, right, top, bottom, single] = key.split(":");
    return {
        left: Number(left),
        right: Number(right),
        isTop: top === "t",
        isBottom: bottom === "b",
        isSingle: single === "s",
    };
};

export type RangeSegmentPart = "start" | "center" | "end";

/** One pin part's piece of the range outline in a row. */
export interface RangeSegment {
    part: RangeSegmentPart;
    /** The part's columns between its edge and the segment: before it, or after it for end pins. */
    offsetColumnIds: string[];
    columnIds: string[];
    hasStartEdge: boolean;
    hasEndEdge: boolean;
}

/**
 * Splits a row's range columns by pin part: pinned pieces stick with their
 * columns while the center piece scrolls under them. Only the range's
 * outermost pieces draw its side edges.
 */
export const getRangeSegments = ({
    columnIds,
    left,
    right,
}: {
    columnIds: Readonly<Record<RangeSegmentPart, readonly string[]>>;
    left: number;
    right: number;
}): RangeSegment[] => {
    const parts: RangeSegmentPart[] = ["start", "center", "end"];
    const segments: RangeSegment[] = [];
    let partStart = 0;
    for (const part of parts) {
        const ids = columnIds[part];
        const from = Math.max(left, partStart) - partStart;
        const to = Math.min(right, partStart + ids.length - 1) - partStart;
        if (from <= to) {
            segments.push({
                part,
                offsetColumnIds:
                    part === "end"
                        ? ids.slice(to + 1)
                        : [
                              ...(part === "center" ? columnIds.start : []),
                              ...ids.slice(0, from),
                          ],
                columnIds: ids.slice(from, to + 1),
                hasStartEdge: partStart + from === left,
                hasEndEdge: partStart + to === right,
            });
        }
        partStart += ids.length;
    }
    return segments;
};
